import { lightOn, type ChunkSource, type LightWhen } from "daedalus-core";
import { gameDayOf } from "../time/game-clock.ts";
import { ambientAt, newAmbient, nightLevel } from "./ambient.ts";
import type { EnergyDraw, EnergyPoint, EnergySource } from "../energy/source.ts";

/**
 * The light sources of a map (ADR-141's amendment of 2026-11-01, decision 6): every placed copy of a frame whose light has its
 * energy source switch on, a fire first and any other kind by the same switch. The sibling of the puddle water module
 * (`weather/puddle-water.ts`): stable keys, an index of buckets, `sourceAt` and `sourcesNear`, the amount at a time and the
 * overrides a draw, a douse or a relight leave. The compile seeds the sources; the map server owns the overrides. Pure: the
 * caller supplies the time and whether it is dark.
 */

/**
 * The energy source kinds OdysseyTales registers for the Asset Studio's Light tab to offer (decision 6), fire first; data
 * may hold any lower-case kind, and `litFireWithin` reads `fire` by name.
 */
export const ENERGY_SOURCE_KINDS = ["fire", "lamp", "crystal"] as const;

/**
 * The kind of source that is a fire: the one kind `litFireWithin` reads by name and a fire draw takes from.
 */
export const FIRE_KIND = "fire";

/**
 * A source's most energy per pixel of its light's radius.
 */
export const SOURCE_ENERGY_PER_PX = 1;

/**
 * The share of its most energy a source recovers each minute after a draw.
 */
export const SOURCE_RECOVERY_PER_MINUTE = 0.05;

const MINUTE_MS = 60_000;
const BUCKET_PX = 512;
const BUCKET_STRIDE = 0x10000;


/**
 * One light source: its stable key (its kind and the placed prop's id, `fire:<id>`), its kind, its anchor in world pixels, its size (the light's
 * radius), its heat (the light's strength), its most energy and when it is lit.
 */
export interface LightSource {
  readonly key: string;
  readonly kind: string;
  readonly x: number;
  readonly y: number;
  readonly size: number;
  readonly heat: number;
  readonly maxEnergy: number;
  readonly when: LightWhen;
}

/**
 * What happened to one source that the compile does not know: energy drawn as of a time (a lit source recovers from then),
 * and the time it went out (drawn to zero; it stays out, at no energy, until relit).
 */
export interface SourceOverride {
  readonly drawn?: number;
  readonly drawnAt?: number;
  readonly outAt?: number;
}

/**
 * A map's light sources, indexed for the queries, with how many of them are fires (`fires`), so a fire query on a map
 * with none answers at once.
 */
export interface SourceIndex {
  readonly mapId: string;
  readonly sources: readonly LightSource[];
  readonly byKey: ReadonlyMap<string, LightSource>;
  readonly buckets: ReadonlyMap<number, readonly LightSource[]>;
  readonly fires: number;
}

/**
 * Everything a query needs: the index, the overrides in force and whether it is dark at a time (for a dusk-to-dawn source).
 */
export interface SourceWorld {
  readonly index: SourceIndex;
  readonly overrides: ReadonlyMap<string, SourceOverride>;
  readonly darkAt: (epochMs: number) => boolean;
}

/**
 * The bucket key of a bucket column and row.
 *
 * @param bx Column.
 * @param by Row.
 * @returns The key.
 */
function bucketKey(bx: number, by: number): number {
  return (by + 1) * BUCKET_STRIDE + bx + 1;
}

/**
 * Indexes a map's sources, as its server chunks list them, by key and by a grid of buckets 512 pixels wide.
 *
 * @param mapId The map's id.
 * @param listed The chunks' sources.
 * @returns The index.
 */
export function indexSources(mapId: string, listed: readonly ChunkSource[]): SourceIndex {
  const sources: LightSource[] = [];
  const byKey = new Map<string, LightSource>();
  const buckets = new Map<number, LightSource[]>();
  let fires = 0;
  for (const entry of listed) {
    const source: LightSource = { key: `${entry.kind}:${entry.id}`, kind: entry.kind, x: entry.x, y: entry.y, size: entry.radius, heat: entry.strength, maxEnergy: entry.radius * SOURCE_ENERGY_PER_PX, when: entry.when };
    sources.push(source);
    byKey.set(source.key, source);
    if (source.kind === FIRE_KIND) fires += 1;
    const key = bucketKey(Math.floor(source.x / BUCKET_PX), Math.floor(source.y / BUCKET_PX));
    const list = buckets.get(key);
    if (list === undefined) buckets.set(key, [source]);
    else list.push(source);
  }
  return { mapId, sources, byKey, buckets, fires };
}

/**
 * The energy a source holds at a time: none while out; else full, less what was drawn, recovering since the draw.
 *
 * @param source The source.
 * @param override Its override, if any.
 * @param epochMs The time.
 * @returns 0 to `maxEnergy`.
 */
function energyAt(source: LightSource, override: SourceOverride | undefined, epochMs: number): number {
  if (override?.outAt !== undefined) return 0;
  if (override?.drawn === undefined) return source.maxEnergy;
  const minutes = Math.max(0, (epochMs - (override.drawnAt ?? epochMs)) / MINUTE_MS);
  return Math.max(0, Math.min(source.maxEnergy, source.maxEnergy - override.drawn + minutes * SOURCE_RECOVERY_PER_MINUTE * source.maxEnergy));
}

/**
 * Whether a source is lit at a time: not out, and for a dusk-to-dawn source, dark.
 *
 * @param world The map's sources.
 * @param source The source.
 * @param epochMs The time.
 * @returns True when lit.
 */
function litAt(world: SourceWorld, source: LightSource, epochMs: number): boolean {
  if (world.overrides.get(source.key)?.outAt !== undefined) return false;
  return source.when === "always" || world.darkAt(epochMs);
}

/**
 * A source as a read answers it.
 *
 * @param world The map's sources.
 * @param source The source.
 * @param epochMs The time.
 * @returns The point.
 */
function pointOf(world: SourceWorld, source: LightSource, epochMs: number): EnergyPoint {
  return { key: source.key, kind: source.kind, x: source.x, y: source.y, amount: energyAt(source, world.overrides.get(source.key), epochMs), max: source.maxEnergy, lit: litAt(world, source, epochMs) };
}

/**
 * The energy the source of a key holds at a time.
 *
 * @param world The map's sources.
 * @param key The source's key.
 * @param epochMs The time.
 * @returns The energy; 0 for a key the map has none of.
 */
export function energyOf(world: SourceWorld, key: string, epochMs: number): number {
  const source = world.index.byKey.get(key);
  return source === undefined ? 0 : energyAt(source, world.overrides.get(key), epochMs);
}

/**
 * The sources within a distance of a point, lit or not, nearest first (ties by key).
 *
 * @param world The map's sources.
 * @param x World x in pixels.
 * @param y World y in pixels.
 * @param radius The distance in pixels, to the source's anchor.
 * @param epochMs The time.
 * @returns The points.
 */
export function sourcesNear(world: SourceWorld, x: number, y: number, radius: number, epochMs: number): EnergyPoint[] {
  const found: { point: EnergyPoint; distance: number }[] = [];
  const x0 = Math.floor((x - radius) / BUCKET_PX);
  const x1 = Math.floor((x + radius) / BUCKET_PX);
  const y0 = Math.floor((y - radius) / BUCKET_PX);
  const y1 = Math.floor((y + radius) / BUCKET_PX);
  for (let by = y0; by <= y1; by += 1) {
    for (let bx = x0; bx <= x1; bx += 1) {
      for (const source of world.index.buckets.get(bucketKey(bx, by)) ?? []) {
        const distance = Math.hypot(source.x - x, source.y - y);
        if (distance <= radius) found.push({ point: pointOf(world, source, epochMs), distance });
      }
    }
  }
  found.sort((a, b) => a.distance - b.distance || (a.point.key < b.point.key ? -1 : a.point.key > b.point.key ? 1 : 0));
  return found.map((entry) => entry.point);
}

/**
 * The source whose size covers a point, the nearest when several do.
 *
 * @param world The map's sources.
 * @param x World x in pixels.
 * @param y World y in pixels.
 * @param epochMs The time.
 * @returns The point, or null.
 */
export function sourceAt(world: SourceWorld, x: number, y: number, epochMs: number): EnergyPoint | null {
  let best: LightSource | null = null;
  let bestDistance = Infinity;
  const bx = Math.floor(x / BUCKET_PX);
  const by = Math.floor(y / BUCKET_PX);
  for (let dy = -1; dy <= 1; dy += 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      for (const source of world.index.buckets.get(bucketKey(bx + dx, by + dy)) ?? []) {
        const distance = Math.hypot(source.x - x, source.y - y);
        if (distance <= source.size && distance < bestDistance) {
          best = source;
          bestDistance = distance;
        }
      }
    }
  }
  return best === null ? null : pointOf(world, best, epochMs);
}

/**
 * Whether a lit fire stands within a distance of a point: the fire kind's convenience, which Drenched's drying reads. It
 * answers as `sourcesNear` would, but walks the buckets and stops at the first lit fire, allocating nothing, and a map with no
 * fire answers at once. A dusk-to-dawn fire is lit only while it is dark (`darkAt`), so it dries only by night.
 *
 * @param world The map's sources.
 * @param x World x in pixels.
 * @param y World y in pixels.
 * @param radius The distance in pixels.
 * @param epochMs The time.
 * @returns True when one does.
 */
export function litFireWithin(world: SourceWorld, x: number, y: number, radius: number, epochMs: number): boolean {
  if (world.index.fires === 0) return false;
  const x0 = Math.floor((x - radius) / BUCKET_PX);
  const x1 = Math.floor((x + radius) / BUCKET_PX);
  const y0 = Math.floor((y - radius) / BUCKET_PX);
  const y1 = Math.floor((y + radius) / BUCKET_PX);
  for (let by = y0; by <= y1; by += 1) {
    for (let bx = x0; bx <= x1; bx += 1) {
      const bucket = world.index.buckets.get(bucketKey(bx, by));
      if (bucket === undefined) continue;
      for (let i = 0; i < bucket.length; i += 1) {
        const source = bucket[i] as LightSource;
        if (source.kind === FIRE_KIND && Math.hypot(source.x - x, source.y - y) <= radius && litAt(world, source, epochMs)) return true;
      }
    }
  }
  return false;
}

/**
 * The world with one source's override set or cleared: the one call a draw, a douse or a relight makes.
 *
 * @param world The map's sources.
 * @param key The source's key.
 * @param override The override, or null to clear it (lit and full).
 * @returns A new world; `world` is unchanged.
 */
export function withSourceOverride(world: SourceWorld, key: string, override: SourceOverride | null): SourceWorld {
  const overrides = new Map(world.overrides);
  if (override === null) overrides.delete(key);
  else overrides.set(key, override);
  return { ...world, overrides };
}

/**
 * The light sources as an `EnergySource` family. It copies the overrides of the world it is given once and then changes its
 * own copy in place on each draw and relight, so a crowd drawing from one hearth costs one map entry each. The map server
 * keeps one per map and is its only caller of `draw` and `restore`.
 *
 * @param initial The map's sources.
 * @returns The family, with `world()` reading its current state.
 */
export function lightSources(initial: SourceWorld): EnergySource & { readonly world: () => SourceWorld } {
  const overrides = new Map(initial.overrides);
  const world: SourceWorld = { ...initial, overrides };
  return {
    name: "light",
    world: () => world,
    read(key, now) {
      const source = world.index.byKey.get(key);
      return source === undefined ? null : pointOf(world, source, now);
    },
    at: (x, y, now) => sourceAt(world, x, y, now),
    near: (x, y, radius, now) => sourcesNear(world, x, y, radius, now),
    draw(key, amount, now): EnergyDraw | null {
      const source = world.index.byKey.get(key);
      if (source === undefined) return null;
      const before = overrides.get(key);
      const held = energyAt(source, before, now);
      if (!Number.isFinite(amount) || amount < 0 || !litAt(world, source, now) || held <= 0) {
        return { point: pointOf(world, source, now), taken: 0 };
      }
      const taken = Math.min(amount, held);
      const left = held - taken;
      overrides.set(key, left <= 0 ? { drawn: source.maxEnergy, drawnAt: now, outAt: now } : { drawn: source.maxEnergy - left, drawnAt: now });
      return { point: pointOf(world, source, now), taken };
    },
    restore(key, now) {
      const source = world.index.byKey.get(key);
      if (source === undefined) return null;
      const before = overrides.get(key);
      if (before?.outAt !== undefined) {
        overrides.set(key, { drawn: source.maxEnergy, drawnAt: now });
      }
      return pointOf(world, source, now);
    },
  };
}

/**
 * Whether it is dark enough at a time for a dusk-to-dawn source to be lit, by the everyday sky (decision 5's rule, which
 * the page follows too; the sky overrides are not read, so a rare event's darker sky does not light a source early). An
 * indoor map is always dark enough.
 *
 * @param indoor Whether the map is indoors.
 * @returns The test, which allocates nothing per call.
 */
export function darkAtFor(indoor: boolean): (epochMs: number) => boolean {
  const scratch = newAmbient();
  return (epochMs) => {
    if (indoor) return true;
    ambientAt(epochMs, "none", scratch);
    return lightOn(scratch.ambient, nightLevel(gameDayOf(epochMs), "none"), "dusk", false) > 0;
  };
}
